import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import * as env from 'config/environment';
import * as consts from 'config/consts';
import {
  getSymbolHaitoInfo,
  getNotifications,
  getSymbolYutaiInfo,
} from 'config/api';
import { useRouting } from 'hooks/useRouting';
import { formatDateForString, formatNumber, formatDate } from 'utils/formatter';
import {
  IncomeGainItemProps,
  getSummaryIncomeGainAmount,
  convertyyyymmToDate,
} from 'pages/DividendDeposit';
import { ProductTypesLabel } from 'components/features/utils/productTypes';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import Loading from 'components/commons/loading/Loading';
import { YutaiInfoProps } from '../dividendDetailPrediction/DividendDetailInfo';
import { commaFormat } from '../new-report/utils/formatter';

export const showNewIcon = (date: string) => {
  const toDay = new Date();
  const oneMonthAgo = toDay.setMonth(toDay.getMonth() - 1);
  const newsDate = new Date(date).getTime();
  if (oneMonthAgo <= newsDate) {
    return <span className="mr-1 text-red-600">New</span>;
  } else {
    <></>;
  }
};

export const HightDividendLink = ({
  symbolName,
  yieldNum,
  tag,
  className,
}: {
  symbolName: string;
  yieldNum: number | null;
  tag: string;
  className?: string;
}) => {
  const dividendYield = yieldNum === null ? 0 : yieldNum.toFixed(2);
  const link = `https://minkabu.jp/stock/search?view=result&page=1&sort_key=dividend_yield&order=desc&minimum_purchase_price[0]=min&minimum_purchase_price[1]=max&market_capitalization[0]=200&market_capitalization[1]=max&dividend_yield[0]=${dividendYield}&dividend_yield[1]=max&capital_adequacy_ratio[0]=40&capital_adequacy_ratio[1]=max&roe[0]=10&roe[1]=max`;

  return (
    <>
      <div className={`${className} mx-2 text-bluegray-700 bg-white`}>
        <a
          href={link}
          id="dividendprediction_slide_bn"
          target="_blank"
          rel="noreferrer">
          <div
            className="py-3 px-4 flex flex-row items-center bg-bluegray-50 border border-gray-300 rounded-lg"
            id={`click_${tag}_recommend_kohaito`}>
            <div>
              <img
                src="/static/images/dividend/dividend_img1.svg"
                alt="配当金アイコン1"
                width="65"
                height="65"
              />
            </div>
            <div className="flex flex-col ml-2 font-bold">
              <div>{symbolName}</div>
              <div className="text-xs">より高配当な銘柄をチェック</div>
            </div>
            <div className="icon-external-link text-xs ml-auto text-gray-500" />
          </div>
        </a>
      </div>
    </>
  );
};

export const ShareholderBenefitsLink = ({
  symbol,
  tag,
  className,
}: {
  symbol: string;
  tag: string;
  className?: string;
}) => {
  return (
    <div className={`${className} mx-2 text-bluegray-700 bg-white`}>
      <a
        href={`https://minkabu.jp/stock/${symbol}/yutai`}
        id="dividendprediction_slide_bn"
        target="_blank"
        rel="noreferrer">
        <div
          className="py-3 px-4 flex flex-row items-center bg-bluegray-50 border border-gray-300 rounded-lg"
          id={`click_${tag}_recommend_yutai`}>
          <div>
            <img
              src="/static/images/dividend/dividend_img2.svg"
              alt="配当金アイコン2"
              width="65"
              height="65"
            />
          </div>
          <div className="ml-2 text-sm font-bold">株主優待をチェック</div>
          <div className="icon-external-link text-xs ml-auto text-gray-500" />
        </div>
      </a>
    </div>
  );
};

const Deposit = ({
  targetIncomeGain,
  targetIncomeGainArr,
  totalAmount,
}: {
  targetIncomeGain: IncomeGainItemProps;
  targetIncomeGainArr: IncomeGainItemProps[];
  totalAmount: number | null;
}) => {
  const initialDisplayCount = 5;
  const initialData = targetIncomeGainArr.filter(
    (_, i) => i < initialDisplayCount
  );
  const [displayDeposititems, setDisplayDeposititems] = useState<
    IncomeGainItemProps[]
  >(initialData);

  const displayMore = () => {
    const data = targetIncomeGainArr.filter(
      (_, i) => i < displayDeposititems.length + 10
    );
    setDisplayDeposititems(data);
  };
  const isAvailableDisplayMore =
    displayDeposititems.length < targetIncomeGainArr.length;

  return (
    <div className="mx-2">
      <div className="flex flex-row items-center">
        <div className="flex flex-row">
          <ProductTypesLabel
            productType={targetIncomeGain.productType}
            cashMargin={consts.CashMargin.現物}
          />
        </div>
        <div className="ml-1 text-xs text-bluegray-500">
          {targetIncomeGain.symbol}
        </div>
      </div>
      <div className="mt-2 pb-1 text-lg font-bold text-blue-850 border-b border-bluegray-300">
        {targetIncomeGain.symbolName}
      </div>
      <div className="mt-4 text-sm text-bluegray-500">入金合計</div>
      <div className="text-36 font-bold">
        {totalAmount === null ? '---' : formatNumber(totalAmount, {})}
        <span className="ml-1">円</span>
      </div>

      <div
        className="grid mt-6 py-1.5 text-xs font-bold text-bluegray-500 bg-bluegray-125"
        style={{ gridTemplateColumns: '1fr 1fr 2fr' }}>
        <div className="pl-2">日付</div>
        <div className="pl-2">口座</div>
        <div className="pl-2">入金額</div>
      </div>
      {displayDeposititems.map((data, i) => {
        return (
          <div
            className="grid mt-2 text-xs"
            style={{ gridTemplateColumns: '1fr 1fr 2fr' }}
            key={i}>
            <div className="pl-2">
              {formatDate(yyyymmddToDate(data.date), 'yyyy/MM/dd')}
            </div>
            <div className="pl-2">{data.brokerName}</div>
            <div className="ml-auto mr-4">
              {data.amount === null ? '---' : formatNumber(data.amount, {})}
              <span className="ml-1">円</span>
            </div>
          </div>
        );
      })}
      {isAvailableDisplayMore ? (
        <div
          className="flex flex-row items-center justify-center mt-2 pt-2 text-gray-500 border-t border-gray-300 cursor-pointer"
          onClick={displayMore}>
          <div className="icon-chevron-down text-xl" />
          <div className="ml-1 text-sm">さらに表示</div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const DividendDetailInfoWithNewsInternal = ({
  symbolHaitoInfo,
  targetIncomeGain,
  targetIncomeGainArr,
  notifications,
  yutaiInfo,
  totalAmount,
  isFetchingSymbolHaitoInfo,
  isFetchingNotifications,
  isFetchingYutaiInfo,
  onCloseRequest,
}: {
  symbolHaitoInfo: SymbolHaitoInfoProps | null;
  targetIncomeGain: IncomeGainItemProps;
  targetIncomeGainArr: IncomeGainItemProps[];
  notifications: NotificationsProps[] | null;
  yutaiInfo?: YutaiInfoProps | null;
  totalAmount: number | null;
  isFetchingSymbolHaitoInfo: boolean;
  isFetchingNotifications: boolean;
  isFetchingYutaiInfo?: boolean;
  onCloseRequest: () => void;
}) => {
  const routing = useRouting();
  const goDetail = (id: string) => {
    return (): void => {
      routing.pushRouting(
        {
          path: `/notificationDetail/4/${id}/${targetIncomeGain.symbol}/DIVIDEND`,
        },
        true,
        window.scrollY
      );
    };
  };

  const getDate = (): string[] | null => {
    if (
      symbolHaitoInfo === null ||
      symbolHaitoInfo === undefined ||
      symbolHaitoInfo.kessanYm === null
    )
      return null;
    const date = convertyyyymmToDate(symbolHaitoInfo.kessanYm);
    if (date === null) return null;

    const year = formatDate(date, 'yyyy');
    const month = formatDate(date, 'MM');
    return [year, month];
  };
  const date = getDate();

  return (
    <div className="w-full flex flex-col py-2 text-bluegray-700">
      <div className="flex flex-row pb-2 px-2">
        <div
          className="icon-close text-bluegray-400 ml-auto text-3xl cursor-pointer"
          onClick={onCloseRequest}></div>
      </div>
      <Deposit {...{ targetIncomeGain, targetIncomeGainArr, totalAmount }} />
      {targetIncomeGain.symbol !== '' ? (
        <>
          {!isFetchingSymbolHaitoInfo &&
          isFetchingYutaiInfo !== undefined &&
          !isFetchingYutaiInfo ? (
            <>
              {symbolHaitoInfo !== null &&
              symbolHaitoInfo !== undefined &&
              symbolHaitoInfo.kessanYm !== null &&
              symbolHaitoInfo.yield !== null &&
              symbolHaitoInfo.yearlyAmount !== null &&
              symbolHaitoInfo.cutOffMonth !== null &&
              symbolHaitoInfo.depositMonth !== null ? (
                <>
                  <div
                    className="grid items-center mt-8 mx-2 border-t border-b border-gray-210"
                    style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
                      配当利回り
                    </div>
                    <div className="ml-auto pr-4 text-sm">
                      {symbolHaitoInfo.yield === null
                        ? '---'
                        : commaFormat(symbolHaitoInfo.yield, {
                            numberOfDecimals: 2,
                          })}
                      %
                    </div>
                  </div>
                  <div
                    className="grid items-center mx-2 border-b border-gray-210"
                    style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
                      {date === null ? (
                        <></>
                      ) : (
                        <>
                          年間配当({date[0]}年{date[1]}月期)
                        </>
                      )}
                    </div>
                    <div className="ml-auto pr-4 text-sm">
                      {symbolHaitoInfo.yearlyAmount === null
                        ? '---'
                        : commaFormat(symbolHaitoInfo.yearlyAmount, {
                            numberOfDecimals: 2,
                          })}
                      円
                    </div>
                  </div>
                  <div
                    className="grid items-center mx-2 border-b border-gray-210"
                    style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
                      権利確定月
                    </div>
                    <div className="ml-auto pr-4 text-sm">
                      {symbolHaitoInfo.cutOffMonth}
                    </div>
                  </div>
                  <div
                    className="grid items-center mx-2 border-b border-gray-210"
                    style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
                      支払予想月
                    </div>
                    <div className="ml-auto pr-4 text-sm">
                      {symbolHaitoInfo.depositMonth}
                    </div>
                  </div>

                  {yutaiInfo !== null && yutaiInfo !== undefined ? (
                    <>
                      <div
                        className="grid items-center mx-2 border-b border-gray-210"
                        style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
                          優待利回り
                        </div>
                        <div className="ml-auto pr-4 text-sm">
                          {yutaiInfo.yutaiYield === null
                            ? '---'
                            : Math.trunc(yutaiInfo.yutaiYield * 100) / 100}
                          %
                        </div>
                      </div>
                      <div
                        className="grid items-center mx-2 border-b border-gray-210"
                        style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <div className="h-full flex items-center bg-bluegray-125">
                          <div className="pl-2 py-1.5 text-xs font-bold">
                            優待情報
                          </div>
                        </div>
                        <div className="ml-auto pl-1 pr-4 text-sm">
                          {yutaiInfo.yutaiTitle === null
                            ? '---'
                            : yutaiInfo.yutaiTitle}
                        </div>
                      </div>
                    </>
                  ) : (
                    <></>
                  )}

                  <div
                    className="grid mt-4 mx-2"
                    style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="mr-1">
                      <a
                        href={`${env.minkabuStockInfoBaseURL}/${targetIncomeGain.symbol}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        <button className="w-full flex justify-center items-center py-2 px-auto font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
                          株価情報
                          <div className="icon-external-link ml-1"></div>
                        </button>
                      </a>
                    </div>
                    <div className="ml-1">
                      <a
                        href={`${env.minkabuStockInfoBaseURL}/${targetIncomeGain.symbol}/dividend`}
                        target="_blank"
                        rel="noopener noreferrer">
                        <button className="w-full flex justify-center items-center py-2 px-auto font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
                          配当情報
                          <div className="icon-external-link ml-1"></div>
                        </button>
                      </a>
                    </div>
                  </div>
                  <div className="mx-2 mt-4 text-xs text-bluegray-500">
                    ※ 配当利回りは前日終値で計算しています。
                  </div>
                </>
              ) : (
                <>
                  <div className="grid mt-4 mx-2">
                    <a
                      href={`${env.minkabuStockInfoBaseURL}/${targetIncomeGain.symbol}`}
                      target="_blank"
                      rel="noopener noreferrer">
                      <button className="w-full flex justify-center items-center py-2 px-auto font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
                        株価情報
                        <div className="icon-external-link ml-1"></div>
                      </button>
                    </a>
                  </div>
                </>
              )}
            </>
          ) : (
            <Loading
              loading={true}
              circularLoading={true}
              style={{ paddingTop: '60px', paddingBottom: '60px' }}
            />
          )}

          <div className="mt-6 mx-2 font-bold">
            {targetIncomeGain.symbolName}を<br />
            保有する方におすすめ
          </div>
          <HightDividendLink
            {...{
              symbolName: targetIncomeGain.symbolName,
              yieldNum:
                symbolHaitoInfo === null || symbolHaitoInfo === undefined
                  ? 0
                  : symbolHaitoInfo.yield,
              tag: 'deposit',
              className: 'mt-2',
            }}
          />
          {yutaiInfo !== null &&
          yutaiInfo !== undefined &&
          !isFetchingYutaiInfo ? (
            <ShareholderBenefitsLink
              {...{
                symbol: targetIncomeGain.symbol,
                tag: 'deposit',
                className: 'mt-2 mb-4',
              }}
            />
          ) : (
            <></>
          )}

          <div className="mt-12 px-2 font-bold">配当金関連ニュース</div>
          <div className="mt-4 border-t border-gray-300" />
          {notifications !== null &&
          notifications !== undefined &&
          !isFetchingNotifications ? (
            <>
              {notifications.map((data, i) => {
                const title = data.title.replace(/^【.*\)】/g, '');
                return (
                  <div
                    className="flex flex-row items-center justify-between py-2 px-2 border-b border-gray-300 cursor-pointer"
                    onClick={goDetail(data.id)}
                    key={i}>
                    <div className="flex flex-col text-sm">
                      <div className="line-clamp-1">{title}</div>
                      <div className="text-gray-500">
                        {showNewIcon(data.insTime)}
                        {formatDateForString(data.insTime, 'M/d HH:mm')}
                      </div>
                    </div>
                    <div className="icon-chevron-right flex text-2xl text-gray-500 ml-2"></div>
                  </div>
                );
              })}
            </>
          ) : (
            <Loading
              loading={true}
              circularLoading={true}
              style={{ paddingTop: '120px', paddingBottom: '120px' }}
            />
          )}
          <div className="mt-4 mx-2">
            <a
              href={`${env.minkabuStockInfoBaseURL}/${targetIncomeGain.symbol}/news`}
              target="_blank"
              rel="noopener noreferrer">
              <button className="w-full flex justify-center items-center py-2 px-8 mb-16 font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
                この銘柄のニュースを見る
                <div className="icon-external-link ml-1"></div>
              </button>
            </a>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

const DividendDetailInfoWithNewsDomesticEtfEtnReit = ({
  targetIncomeGain,
  targetIncomeGainArr,
  totalAmount,
  onCloseRequest,
}: {
  targetIncomeGain: IncomeGainItemProps;
  targetIncomeGainArr: IncomeGainItemProps[];
  totalAmount: number | null;
  onCloseRequest: () => void;
}) => {
  const {
    data: symbolHaitoInfo,
    isFetching: isFetchingSymbolHaitoInfo,
  } = useQuery(
    'getSymbolHaitoInfoDividendDepositEtfEtnReitAPI',
    () => getSymbolHaitoInfoAPI(targetIncomeGain.symbol),
    {
      refetchOnWindowFocus: false,
    }
  );

  const func = 'DIVIDEND';
  const { data: notifications, isFetching: isFetchingNotifications } = useQuery(
    'getNotificationsDividendDepositAPI',
    () => getNotificationsAPI(targetIncomeGain.symbol, func),
    {
      refetchOnWindowFocus: false,
    }
  );

  return (
    <DividendDetailInfoWithNewsInternal
      {...{
        targetIncomeGain,
        targetIncomeGainArr,
        symbolHaitoInfo,
        notifications,
        totalAmount,
        isFetchingSymbolHaitoInfo,
        isFetchingNotifications,
        onCloseRequest,
      }}
    />
  );
};

const DividendDetailInfoWithNewsDomesticStocks = ({
  targetIncomeGain,
  targetIncomeGainArr,
  totalAmount,
  onCloseRequest,
}: {
  targetIncomeGain: IncomeGainItemProps;
  targetIncomeGainArr: IncomeGainItemProps[];
  totalAmount: number | null;
  onCloseRequest: () => void;
}) => {
  const {
    data: symbolHaitoInfo,
    isFetching: isFetchingSymbolHaitoInfo,
  } = useQuery(
    'getSymbolHaitoInfoDividendDepositDomesticStocksAPI',
    () => getSymbolHaitoInfoAPI(targetIncomeGain.symbol),
    {
      refetchOnWindowFocus: false,
    }
  );

  const func = 'DIVIDEND';
  const { data: notifications, isFetching: isFetchingNotifications } = useQuery(
    'getNotificationsDividendDepositDomesticStocksAPI',
    () => getNotificationsAPI(targetIncomeGain.symbol, func),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: yutaiInfo, isFetching: isFetchingYutaiInfo } = useQuery(
    'getSymbolYutaiInfoDividendDepositAPI',
    () => getSymbolYutaiInfoAPI(targetIncomeGain.symbol),
    {
      refetchOnWindowFocus: false,
    }
  );

  return (
    <DividendDetailInfoWithNewsInternal
      {...{
        targetIncomeGain,
        targetIncomeGainArr,
        symbolHaitoInfo,
        notifications,
        yutaiInfo,
        totalAmount,
        isFetchingSymbolHaitoInfo,
        isFetchingNotifications,
        isFetchingYutaiInfo,
        onCloseRequest,
      }}
    />
  );
};

const DividendDetailInfoNoNews = ({
  targetIncomeGain,
  targetIncomeGainArr,
  totalAmount,
  onCloseRequest,
}: {
  targetIncomeGain: IncomeGainItemProps;
  targetIncomeGainArr: IncomeGainItemProps[];
  totalAmount: number | null;
  onCloseRequest: () => void;
}) => {
  const isInvestmentTrustDataCooperation =
    targetIncomeGain.productType === consts.productTypes.投資信託 &&
    !(
      targetIncomeGain.symbol === '' ||
      /^MONO/.test(targetIncomeGain.symbol) ||
      /^DC/.test(targetIncomeGain.symbol)
    );

  return (
    <div className="w-full flex flex-col py-2 text-bluegray-700">
      <div className="flex flex-row pb-2 px-2">
        <div
          className="icon-close text-bluegray-400 ml-auto text-3xl cursor-pointer"
          onClick={onCloseRequest}></div>
      </div>
      <Deposit {...{ targetIncomeGain, targetIncomeGainArr, totalAmount }} />
      {isInvestmentTrustDataCooperation ? (
        <div className="mt-4 mx-2">
          <a
            href={`${env.minkabuInvestmentTrustFundInfoBaseURL}/${targetIncomeGain.symbol}`}
            target="_blank"
            rel="noopener noreferrer">
            <button className="w-full flex justify-center items-center py-2 px-8 mb-16 font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
              ファンド情報を見る
              <div className="icon-external-link ml-1"></div>
            </button>
          </a>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export const DividendDetailInfo = ({
  menuHide,
  hideAnimate,
  targetIncomeGain,
  arrIncomeGain,
}: {
  menuHide: () => void;
  hideAnimate: boolean;
  targetIncomeGain: IncomeGainItemProps;
  arrIncomeGain: IncomeGainItemProps[];
}): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (hideAnimate) setIsOpen(false);
  }, [hideAnimate]);

  const onCloseRequest = (): void => {
    setIsOpen(false);
  };

  const onAnimated = (): void => {
    if (!isOpen) menuHide();
  };

  const animationOption =
    ' animation-200ms animation-ease-in-out animation-fill-forwards';

  const showOrHideAnimation = isOpen
    ? { content: 'animation-appear-from-right', mask: 'animation-fadeIn' }
    : { content: 'animation-disappear-to-right', mask: 'animation-fadeOut' };
  showOrHideAnimation.content += animationOption;
  showOrHideAnimation.mask += animationOption;

  const targetIncomeGainArr = arrIncomeGain
    .filter(
      data =>
        data.symbol === targetIncomeGain.symbol &&
        data.symbolName === targetIncomeGain.symbolName
    )
    .sort((first, second) => {
      const firstAmount = yyyymmddToDate(first.date)?.getTime();
      const secondAmount = yyyymmddToDate(second.date)?.getTime();

      if (firstAmount === undefined || secondAmount === undefined) return 0;
      return firstAmount < secondAmount ? 1 : -1;
    });

  const isAmountNull = targetIncomeGainArr.every(data => data.amount === null);
  const totalAmount = isAmountNull
    ? null
    : getSummaryIncomeGainAmount(targetIncomeGainArr);

  const content = () => {
    switch (targetIncomeGain.productType) {
      case consts.productTypes.国内株式:
        return (
          <DividendDetailInfoWithNewsDomesticStocks
            {...{
              onCloseRequest,
              targetIncomeGain,
              targetIncomeGainArr,
              totalAmount,
            }}
          />
        );
      case consts.productTypes['国内ETF・ETN']:
      case consts.productTypes.国内REIT:
        return (
          <DividendDetailInfoWithNewsDomesticEtfEtnReit
            {...{
              onCloseRequest,
              targetIncomeGain,
              targetIncomeGainArr,
              totalAmount,
            }}
          />
        );
      default:
        return (
          <DividendDetailInfoNoNews
            {...{
              onCloseRequest,
              targetIncomeGain,
              targetIncomeGainArr,
              totalAmount,
            }}
          />
        );
    }
  };

  return (
    <div
      className="fixed left-0 ui-h-screen w-screen z-20"
      style={{ top: '0' }}>
      <div className="overflow-y-auto relative h-full w-full">
        <div
          className={`fixed left-0 bg-gray-800 h-full w-full ${showOrHideAnimation.mask}`}
          onClick={onCloseRequest}
        />
        <div
          className={`lg:flex hidden fixed right-0 h-full bg-white overflow-y-auto w-64 ${showOrHideAnimation.content}`}
          style={{
            width: '35%',
            height: '100%',
          }}
          onAnimationEnd={onAnimated}>
          {content()}
        </div>
        <div
          className={`flex lg:hidden fixed right-0 h-full bg-white overflow-y-auto w-64 ${showOrHideAnimation.content}`}
          style={{
            width: '90%',
            height: '100%',
          }}
          onAnimationEnd={onAnimated}>
          {content()}
        </div>
      </div>
    </div>
  );
};

export type SymbolHaitoInfoProps = {
  kessanYm: string | null; // yyyymm
  yield: number | null;
  yearlyAmount: number | null;
  cutOffMonth: string | null;
  depositMonth: string | null;
};

const getSymbolHaitoInfoAPI = async (
  symbol: string
): Promise<SymbolHaitoInfoProps> => {
  const ret_ = await getSymbolHaitoInfo(symbol);
  const ret = {
    // memo: apiレスポンスを yyyy.mm -> yyyymm に変換する
    kessanYm: ret_.KessanYm !== null ? ret_.KessanYm.replace('.', '') : null,
    yield: ret_.Yield,
    yearlyAmount: ret_.YearlyAmount,
    cutOffMonth: ret_.CutOffMonth,
    depositMonth: ret_.DepositMonth,
  } as SymbolHaitoInfoProps;

  return ret;
};

export type NotificationsProps = {
  id: string;
  type: number;
  title: string;
  content: string;
  readStatus: boolean;
  insTime: string;
  updTime: string;
};

const getNotificationsAPI = async (
  symbol: string,
  func: string
): Promise<NotificationsProps[] | null> => {
  const ret_ = await getNotifications(symbol, func);
  if (ret_ === null) return null;

  const ret = ret_?.Items.map(item => {
    return {
      id: item.ID,
      type: item.Type,
      title: item.Title,
      content: item.Content,
      readStatus: item.ReadStatus,
      insTime: item.InsTime,
      updTime: item.UpdTime,
    };
  }) as NotificationsProps[];

  return ret;
};

const getSymbolYutaiInfoAPI = async (
  symbol: string
): Promise<YutaiInfoProps | null> => {
  const ret_ = await getSymbolYutaiInfo(symbol);
  if (ret_ === null) return null;

  const ret = {
    yutaiTitle: ret_.YutaiTitle,
    yutaiYield: ret_.YutaiYield,
  } as YutaiInfoProps;

  return ret;
};
